import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { useState } from 'react'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import { SAVE_WEBFLOW_FORMS_INTEGRATION, REMOVE_LOCATION_INTEGRATION } from '../ops-graphql'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormInput from 'app/components/form/FormInput'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import { CopyToClipboard } from 'app/components/shared/CopyToClipboard'
import { Tooltip } from 'app/components/Tooltip'
import type { LocationJson, LocationType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { gqlOpsMutation } from 'app/lib/opsGraphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { ObjectId } from 'lib/ObjectId'
import { OptionType } from 'types/OptionType'

const createSchema = z.object({
  locationId: z.string().min(1, { message: `A Location is required` }),
  webflowFormsKey: z.string().min(1, { message: `An API key is required` }),
})

export const validator = withZod(createSchema)

export function WebflowFormModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: {
    locationId: string
    locationName: string
    webflowFormsKey: LocationType[`webflowFormsKey`]
  }
}) {
  const [location, setLocation] = useState<OptionType | undefined>(
    currentValues
      ? { id: currentValues.locationId, name: currentValues.locationName, value: currentValues.locationId }
      : undefined,
  )
  const [confirm] = useConfirmation()
  const [apiKey, setApiKey] = useState<string>(currentValues?.webflowFormsKey || ObjectId())

  const onSave = async () => {
    const locationId = currentValues?.locationId || (location?.value as string)
    if (!locationId || !apiKey) return

    const result = await gqlOpsMutation(SAVE_WEBFLOW_FORMS_INTEGRATION, {
      locationId,
      webflowFormsKey: apiKey,
      isNew: !currentValues,
    })
    const data = result.data?.saveWebflowFormsIntegration
    if (data?.success) {
      notifySuccess(`Webflow Forms integration saved`)
      onAdd()
      closeModal()
    } else {
      notifyError(`Failed to save integration: ${data?.error || result.error || 'Unknown error'}`)
    }
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onRemove = () => {
    return confirm({
      title: `Remove Webflow Forms Integration`,
      description: `Removing this integration will also remove all associated data. This may result in contacts being removed. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      gqlOpsMutation(REMOVE_LOCATION_INTEGRATION, {
        locationId: currentValues?.locationId!,
        integration: `webflow`,
      }).then((result) => {
        if (result.data?.removeLocationIntegration?.success) {
          notifySuccess(`Webflow Forms integration removed`)
          onAdd()
          closeModal()
        } else {
          notifyError(`Failed to remove integration: ${result.error || 'Unknown error'}`)
        }
      }),
    )
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={validator}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={onSave}
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        hiddenInputs={currentValues ? [{ name: `locationId`, value: currentValues.locationId }] : undefined}
      >
        {currentValues ? (
          <FormSelect
            cols={6}
            name="locationId"
            label="Trackable Location"
            options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
            value={currentValues.locationId}
            disabled
            labelClassName="text-body-sm text-strong"
          />
        ) : (
          <FormComboAsync
            cols={6}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={location}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
                meta: {
                  isUsed: !!hit.webflowFormsKey,
                  tooltipForDisabledOption: `This location is already connected to Webflow Forms`,
                },
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}

        {location && (
          <div className="col-span-6 grid grid-cols-6 gap-y-2">
            <FormInput
              type="text"
              cols={5}
              name="webflowFormsKey"
              label="Webhook API Key"
              labelClassName="text-body-sm text-strong"
              className="!bg-surface"
              value={apiKey}
              disabled={true}
              newLineError={false}
              showErrorIcon
            />
            <div className="col-span-1 flex items-stretch justify-center gap-x-2 pt-9">
              <div className="h-7 w-7 cursor-pointer">
                <CopyToClipboard text={apiKey} />
              </div>
              {currentValues && (
                <Tooltip message="Generate a new key" className="h-7 w-7">
                  <ArrowPathIcon
                    className={classNames(`cursor-pointer`)}
                    onClick={(event) => {
                      let target = event.currentTarget
                      target.classList.add(`animate-spin`)
                      setApiKey(ObjectId())
                      setTimeout(() => target.classList.remove(`animate-spin`), 500)
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
            )}
            <Button type="submit" form="integration-form" label="Save" mode="primary" size="md" disabled={!location} />
          </div>
        </div>
      </div>
    </>
  )
}
